import React, { useState, useMemo } from "react";
import axiosInstance from "../config/axiosConfig";
import BuscarPaciente from "./components/BuscarPaciente";
import BuscarAuxiliar from "./components/BuscarAuxiliar";
import { getFechaMinimaAcuerdo, validarFechaAcuerdo, getFechaLocalHoy } from "../utils/acuerdoMutuoFechas";
import { getAuxiliarNombreCompleto } from "../utils/noEnviarPaciente";
import { formatDate, formatMoney } from "../utils/utils";
/* global gdAlertify */

// ============================================================================
// "Editar Acuerdos" (ruta /editar-acuerdos).
//
// Muestra la rotación del paciente con sus slots coloreados por auxiliar y una
// vista previa del calendario desde la fecha efectiva. Al guardar, el back
// (POST /acuerdo-mutuo/editar-rotacion/*) hace todo en UNA transacción:
//   - solo cierra/crea los acuerdos que cambian (el resto queda intacto),
//   - actualiza el patrón sin tocar la ficha del paciente,
//   - los acuerdos nuevos conservan los valores del auxiliar.
// Antes de aplicar se muestra el plan (qué se cierra, qué se mantiene, qué se crea).
// ============================================================================

const COLORES_AUXILIAR = ["#4C6EF5", "#12B886", "#F59F00", "#E64980", "#7048E8", "#15AABF", "#FA5252", "#82C91E"];

const DIAS = [
    { value: "Mon", label: "Lun" }, { value: "Tue", label: "Mar" }, { value: "Wed", label: "Mié" },
    { value: "Thu", label: "Jue" }, { value: "Fri", label: "Vie" }, { value: "Sat", label: "Sáb" }, { value: "Sun", label: "Dom" },
];
const DIA_INDICE = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PRESETS_DIAS = [
    { id: "lunvie", label: "Lunes a Viernes", dias: ["Mon", "Tue", "Wed", "Thu", "Fri"] },
    { id: "todos", label: "Todos los días", dias: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] },
    { id: "finde", label: "Fines de semana", dias: ["Sat", "Sun"] },
];
const PERIODO_OPCIONES_SLOT = [
    { value: "day", label: "Día" }, { value: "night", label: "Noche" },
    { value: "full", label: "Día y Noche" }, { value: "mixto", label: "Mixto (día a día)" },
    { value: "libre", label: "Descanso (libre)" },
];
const OPCIONES_DIARIAS = [
    { value: "full", label: "Día/Noche" }, { value: "day", label: "Día" },
    { value: "night", label: "Noche" }, { value: "libre", label: "Libre" },
];
const LABEL_PERIODO = { day: "Día", night: "Noche", full: "Día y Noche", libre: "Descanso", mixto: "Mixto" };
const OPCIONES_SEMANAS = [2, 3, 4, 6];
const COLOR_DESCANSO = "#e9ecef";

const fechaLocal = (iso) => {
    const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
};
const iso = (f) => `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}-${String(f.getDate()).padStart(2, "0")}`;

const esSlotLibre = (s) => s.period === "libre"
    || (s.period === "mixto" && (s.dailyPeriods || []).length > 0 && s.dailyPeriods.every(d => d === "libre"));
const esMixto = (s) => s.period === "mixto" && !esSlotLibre(s);
// Mitades que existen en un slot mixto según su configuración día a día.
const mixtoTieneDia = (s) => (s.dailyPeriods || []).some(d => d === "day" || d === "full");
const mixtoTieneNoche = (s) => (s.dailyPeriods || []).some(d => d === "night" || d === "full");

function parseSlotsDelPatron(turnos) {
    if (!turnos) return [];
    if (turnos.type === "fixed") return [];
    try {
        let pattern = typeof turnos.rotation_pattern === "string" ? JSON.parse(turnos.rotation_pattern) : turnos.rotation_pattern;
        if (!Array.isArray(pattern)) pattern = [pattern];
        return pattern.map((p) => {
            const libre = esSlotLibre(p);
            return {
                period: libre ? "libre" : p.period,
                days: parseInt(p.days) || 1,
                start: p.start || "08:00",
                end: p.end || "20:00",
                dailyPeriods: p.period === "mixto" && !libre ? (p.dailyPeriods || []) : undefined,
                auxiliarId: null,
            };
        });
    } catch (e) {
        console.error("Error parseando rotation_pattern:", e);
        return [];
    }
}

// Firma del patrón para saber si cambió (mismo criterio que el back: ignora estados y orden de días).
const firmaPatron = ({ tipo, serviceDays, fixedPeriod, slots }) => JSON.stringify([
    tipo, [...serviceDays].sort(),
    tipo === "fixed" ? fixedPeriod : slots.map(s => esSlotLibre(s)
        ? ["libre", s.days]
        : [s.period, s.days, s.period === "mixto" ? "" : s.start, s.period === "mixto" ? "" : s.end, s.period === "mixto" ? s.dailyPeriods : []]),
]);

// Vista previa con el mismo cálculo del back (Utils::fechaCorrespondeASlotIndividual): se
// cuentan solo los días de servicio desde el inicio de la rotación.
function generarPreview({ tipo, serviceDays, fixedPeriod, slots, auxiliarFijo, inicioRotacion, fechaEfectiva }, numDias) {
    const diasSet = new Set(serviceDays);
    const efectiva = fechaLocal(fechaEfectiva);
    const inicioGrilla = new Date(efectiva);
    inicioGrilla.setDate(inicioGrilla.getDate() - ((efectiva.getDay() + 6) % 7)); // lunes de esa semana
    const ancla = fechaLocal(inicioRotacion);

    const cicloTotal = slots.reduce((sum, s) => sum + (parseInt(s.days) || 0), 0);
    // días de servicio entre el ancla y el inicio de la grilla
    let contador = 0;
    for (const c = new Date(ancla); c < inicioGrilla; c.setDate(c.getDate() + 1)) {
        if (diasSet.has(DIA_INDICE[c.getDay()])) contador++;
    }

    const dias = [];
    for (let i = 0; i < numDias; i++) {
        const fecha = new Date(inicioGrilla);
        fecha.setDate(fecha.getDate() + i);
        const antes = fecha < efectiva;
        const servicio = diasSet.has(DIA_INDICE[fecha.getDay()]) && fecha >= ancla;
        // auxDia / auxNoche: quién cubre cada mitad ese día (en un slot mixto pueden ser distintos)
        let dia = { fecha, antes, periodo: null, slot: null, auxDia: null, auxNoche: null };

        if (servicio) {
            if (tipo === "fixed") {
                dia = { ...dia, periodo: fixedPeriod, slot: 1, auxDia: auxiliarFijo, auxNoche: auxiliarFijo };
            } else if (cicloTotal > 0) {
                let pos = contador % cicloTotal;
                let idx = 0;
                while (pos >= (parseInt(slots[idx].days) || 1)) {
                    pos -= parseInt(slots[idx].days) || 1;
                    idx++;
                }
                const s = slots[idx];
                const mixto = esMixto(s);
                const periodo = esSlotLibre(s) ? "libre" : (mixto ? (s.dailyPeriods?.[pos] || "libre") : s.period);
                dia = {
                    ...dia, periodo, slot: idx + 1,
                    auxDia: periodo === "libre" ? null : (mixto ? s.auxDia : s.auxiliarId),
                    auxNoche: periodo === "libre" ? null : (mixto ? s.auxNoche : s.auxiliarId),
                };
            }
            contador++;
        }
        dias.push(dia);
    }
    return dias;
}

function AgregarAuxiliarInline({ pacienteId, onAgregar }) {
    const [mostrar, setMostrar] = useState(false);
    if (!mostrar) {
        return (
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setMostrar(true)}>
                <i className="bi bi-plus-circle me-1"></i>Agregar auxiliar
            </button>
        );
    }
    return (
        <div style={{ minWidth: 260 }}>
            <BuscarAuxiliar pacienteId={pacienteId} onSelect={(aux) => { onAgregar(aux); setMostrar(false); }} />
        </div>
    );
}

const GestionSlotsDemo = () => {
    const [paciente, setPaciente] = useState(null);
    const [acuerdosActivos, setAcuerdosActivos] = useState([]);
    const [auxiliaresPalette, setAuxiliaresPalette] = useState([]); // [{id, nombre, color}]
    const [serviceDays, setServiceDays] = useState([]);
    const [tipo, setTipo] = useState("rotative");
    const [fixedPeriod, setFixedPeriod] = useState("day");
    const [auxiliarFijo, setAuxiliarFijo] = useState(null);
    const [slots, setSlots] = useState([]);
    const [firmaOriginal, setFirmaOriginal] = useState("");
    const [inicioRotacionActual, setInicioRotacionActual] = useState("");
    const [inicioRotacionElegido, setInicioRotacionElegido] = useState(null); // null = automático
    const [fechaEfectiva, setFechaEfectiva] = useState(getFechaLocalHoy());
    const [observacion, setObservacion] = useState("");
    const [semanas, setSemanas] = useState(2);
    const [cargando, setCargando] = useState(false);
    const [procesando, setProcesando] = useState(false);
    const [plan, setPlan] = useState(null);

    const colorDe = (auxId) => auxiliaresPalette.find(a => a.id === auxId)?.color || "#adb5bd";
    const nombreDe = (auxId) => auxiliaresPalette.find(a => a.id === auxId)?.nombre || "Sin asignar";

    const cargarPaciente = async (p) => {
        setPlan(null);
        if (!p) {
            setPaciente(null); setSlots([]); setAuxiliaresPalette([]); setAcuerdosActivos([]);
            return;
        }
        setCargando(true);
        try {
            const [respPaciente, respSlots] = await Promise.all([
                axiosInstance.get("/paciente/busqueda", { params: { id: p.id } }),
                axiosInstance.get(`/acuerdo-mutuo/slots-paciente/${p.id}`),
            ]);
            const fullData = respPaciente.data.data[0] || {};
            const acuerdos = respSlots.data.data || [];
            const turnos = fullData.turnos;
            if (!turnos) {
                gdAlertify.notiError("El paciente no tiene un patrón de turnos configurado.");
            }

            const palette = [];
            acuerdos.forEach(a => {
                if (!palette.find(u => u.id === a.idAuxiliar)) {
                    palette.push({ id: a.idAuxiliar, nombre: a.auxiliarNombre, color: COLORES_AUXILIAR[palette.length % COLORES_AUXILIAR.length] });
                }
            });

            // Slot -> auxiliar por parte: periodo_cubierto 1 = solo día, 2 = solo noche, 3 = ambos.
            const slotsAux = {};
            acuerdos.forEach(a => {
                const parte = { 1: "dia", 2: "noche" }[Number(a.periodo_cubierto)] || "ambos";
                String(a.patternIndex).split(",").map(x => parseInt(x.trim())).filter(n => !isNaN(n)).forEach(idx => {
                    slotsAux[idx] = { ...(slotsAux[idx] || {}), [parte]: a.idAuxiliar };
                });
            });

            const tipoPatron = turnos?.type === "fixed" ? "fixed" : "rotative";
            const nuevosSlots = parseSlotsDelPatron(turnos).map((s, idx) => {
                const asig = slotsAux[idx] || {};
                if (esSlotLibre(s)) return { ...s, auxiliarId: null };
                if (esMixto(s)) return { ...s, auxiliarId: null, auxDia: asig.dia ?? asig.ambos ?? null, auxNoche: asig.noche ?? asig.ambos ?? null };
                return { ...s, auxiliarId: asig.ambos ?? asig.dia ?? asig.noche ?? null };
            });
            const dias = turnos?.days_service ? turnos.days_service.split(",").map(d => d.trim()).filter(Boolean) : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            const periodoFijo = turnos?.period || "day";

            const tutorPago = (fullData.tutores || []).find(t => Number(t.tipo_tutor) === 1 || Number(t.tipo_tutor) === 3);

            setPaciente(fullData);
            setAcuerdosActivos(acuerdos);
            setAuxiliaresPalette(palette);
            setTipo(tipoPatron);
            setFixedPeriod(periodoFijo);
            setAuxiliarFijo(tipoPatron === "fixed" ? (slotsAux[0]?.ambos ?? slotsAux[0]?.dia ?? null) : null);
            setSlots(nuevosSlots);
            setServiceDays(dias);
            setInicioRotacionActual(String(tutorPago?.fechaInicioServicio || "").slice(0, 10));
            setInicioRotacionElegido(null);
            setFirmaOriginal(firmaPatron({ tipo: tipoPatron, serviceDays: dias, fixedPeriod: periodoFijo, slots: nuevosSlots }));
        } catch (error) {
            console.error("Error cargando paciente/slots:", error);
            gdAlertify.notiError("Error al cargar el paciente");
        } finally {
            setCargando(false);
        }
    };

    const agregarAuxiliarPalette = (aux) => {
        if (auxiliaresPalette.find(a => a.id === aux.id)) return;
        setAuxiliaresPalette(prev => [...prev, { id: aux.id, nombre: getAuxiliarNombreCompleto(aux), color: COLORES_AUXILIAR[prev.length % COLORES_AUXILIAR.length] }]);
    };
    const quitarAuxiliarPalette = (auxId) => {
        setAuxiliaresPalette(prev => prev.filter(a => a.id !== auxId));
        setSlots(prev => prev.map(s => ({
            ...s,
            auxiliarId: s.auxiliarId === auxId ? null : s.auxiliarId,
            auxDia: s.auxDia === auxId ? null : s.auxDia,
            auxNoche: s.auxNoche === auxId ? null : s.auxNoche,
        })));
        if (auxiliarFijo === auxId) setAuxiliarFijo(null);
    };

    const toggleDia = (dia) => setServiceDays(prev => prev.includes(dia) ? prev.filter(d => d !== dia) : [...prev, dia]);
    const addSlot = () => setSlots(prev => {
        const base = prev.find(s => !esSlotLibre(s)) || { start: "08:00", end: "20:00" };
        return [...prev, { period: "full", days: 1, start: base.start || "08:00", end: base.end || "20:00", auxiliarId: null }];
    });
    const removeSlot = (idx) => setSlots(prev => prev.filter((_, i) => i !== idx));
    const updateSlot = (idx, campo, valor) => setSlots(prev => prev.map((s, i) => {
        if (i !== idx) return s;
        const nuevo = { ...s, [campo]: valor };
        if (campo === "period") {
            if (valor === "mixto" && s.period !== "mixto") {
                // El auxiliar que tenía pasa a cubrir día y noche; luego se pueden separar.
                const aux = s.auxiliarId ?? null;
                nuevo.dailyPeriods = Array.from({ length: parseInt(s.days) || 1 }, () => "full");
                nuevo.auxDia = aux; nuevo.auxNoche = aux; nuevo.auxiliarId = null;
                nuevo.start = ""; nuevo.end = "";
            } else if (valor !== "mixto" && s.period === "mixto") {
                nuevo.auxiliarId = s.auxDia || s.auxNoche || null;
                nuevo.auxDia = null; nuevo.auxNoche = null; nuevo.dailyPeriods = undefined;
                const base = prev.find(x => x.period !== "mixto" && x.period !== "libre" && x.start) || { start: "08:00", end: "20:00" };
                nuevo.start = base.start; nuevo.end = base.end;
            }
            if (valor === "libre") { // descanso no lleva auxiliar
                nuevo.auxiliarId = null; nuevo.auxDia = null; nuevo.auxNoche = null;
            }
        }
        if (campo === "days" && s.period === "mixto") {
            nuevo.dailyPeriods = Array.from({ length: parseInt(valor) || 1 }, (_, k) => s.dailyPeriods?.[k] || "full");
        }
        return nuevo;
    }));
    const updateDiaMixto = (idx, dia, valor) => setSlots(prev => prev.map((s, i) => {
        if (i !== idx) return s;
        const dailyPeriods = [...(s.dailyPeriods || [])];
        dailyPeriods[dia] = valor;
        return { ...s, dailyPeriods };
    }));

    const selectorAuxiliar = (valor, onChange) => (
        <select
            className="form-select form-select-sm"
            style={{ background: valor ? colorDe(valor) : undefined, color: valor ? "#fff" : undefined }}
            value={valor || ""}
            onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
        >
            <option value="">Sin asignar</option>
            {auxiliaresPalette.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
    );

    const patronCambia = useMemo(
        () => paciente && firmaPatron({ tipo, serviceDays, fixedPeriod, slots }) !== firmaOriginal,
        [paciente, tipo, serviceDays, fixedPeriod, slots, firmaOriginal]
    );
    // Inicio de la rotación (día 1 del slot 1), igual que "Fecha de Inicio Rotación" en Nuevo
    // Acuerdo. Automático: la fecha efectiva si cambia el patrón; si no, el inicio actual (el
    // ciclo continúa). Si se elige otra fecha, el ciclo se reinicia desde esa fecha.
    const inicioRotacion = inicioRotacionElegido
        || (patronCambia || !inicioRotacionActual ? fechaEfectiva : inicioRotacionActual);
    const rotacionCambia = patronCambia || inicioRotacion !== inicioRotacionActual;

    const previewDias = useMemo(
        () => (paciente && fechaEfectiva
            ? generarPreview({ tipo, serviceDays, fixedPeriod, slots, auxiliarFijo, inicioRotacion, fechaEfectiva }, semanas * 7)
            : []),
        [paciente, tipo, serviceDays, fixedPeriod, slots, auxiliarFijo, inicioRotacion, fechaEfectiva, semanas]
    );

    const construirPayload = () => ({
        pacienteID: paciente.id,
        fecha: fechaEfectiva,
        inicioRotacion,
        observacion,
        patron: {
            tipo,
            serviceDays,
            fixedPeriod,
            horaInicio: (paciente.turnos?.hora_inicio || "08:00").slice(0, 5),
            horaFin: (paciente.turnos?.hora_fin || "20:00").slice(0, 5),
            phases: slots.map(s => ({
                period: s.period,
                days: s.days,
                start: esSlotLibre(s) || s.period === "mixto" ? "" : s.start,
                end: esSlotLibre(s) || s.period === "mixto" ? "" : s.end,
                dailyPeriods: s.period === "mixto" ? (s.dailyPeriods || []) : [],
            })),
        },
        // parte: "ambos" (día y noche) | "dia" | "noche" (mitades de un slot mixto)
        asignaciones: tipo === "fixed"
            ? (auxiliarFijo ? [{ slot: 0, auxiliarId: auxiliarFijo, parte: "ambos" }] : [])
            : slots.flatMap((s, idx) => {
                if (esSlotLibre(s)) return [];
                if (!esMixto(s)) return s.auxiliarId ? [{ slot: idx, auxiliarId: s.auxiliarId, parte: "ambos" }] : [];
                const auxDia = mixtoTieneDia(s) ? s.auxDia : null;
                const auxNoche = mixtoTieneNoche(s) ? s.auxNoche : null;
                if (auxDia && auxDia === auxNoche) return [{ slot: idx, auxiliarId: auxDia, parte: "ambos" }];
                return [
                    ...(auxDia ? [{ slot: idx, auxiliarId: auxDia, parte: "dia" }] : []),
                    ...(auxNoche ? [{ slot: idx, auxiliarId: auxNoche, parte: "noche" }] : []),
                ];
            }),
    });

    const revisarCambios = async () => {
        if (!paciente) return;
        if (serviceDays.length === 0) {
            gdAlertify.notiError("Seleccione al menos un día de servicio.");
            return;
        }
        if (tipo === "rotative" && (slots.length === 0 || slots.some(s => !s.days || s.days <= 0))) {
            gdAlertify.notiError("Agregue al menos un slot con duración válida.");
            return;
        }
        const errorFecha = validarFechaAcuerdo(fechaEfectiva, "La fecha efectiva");
        if (errorFecha) {
            gdAlertify.notiError(errorFecha);
            return;
        }
        if (inicioRotacion > fechaEfectiva) {
            gdAlertify.notiError("El inicio de la rotación no puede ser posterior a la fecha efectiva.");
            return;
        }

        setProcesando(true);
        try {
            const resp = await axiosInstance.post("/acuerdo-mutuo/editar-rotacion/previsualizar", construirPayload());
            if (resp.data.error) throw new Error(resp.data.message);
            if (!resp.data.data.hayCambios) {
                gdAlertify.notiError("No hay cambios respecto a la rotación y asignación actual.");
                return;
            }
            setPlan(resp.data.data);
        } catch (e) {
            gdAlertify.notiError(e.message || "No se pudo revisar los cambios.");
        } finally {
            setProcesando(false);
        }
    };

    const aplicar = async () => {
        setProcesando(true);
        try {
            const resp = await axiosInstance.post("/acuerdo-mutuo/editar-rotacion/aplicar", construirPayload());
            if (resp.data.error) throw new Error(resp.data.message);
            gdAlertify.notiSuccess("Cambios guardados.");
            setPlan(null);
            setObservacion("");
            cargarPaciente({ id: paciente.id });
        } catch (e) {
            // El back hace rollback completo: si falla, no queda nada a medias.
            gdAlertify.notiError((e.message || "No se pudo guardar.") + " No se aplicó ningún cambio.");
        } finally {
            setProcesando(false);
        }
    };

    const listaSlots = (s) => s.map(i => i + 1).join(", ");
    // periodo_cubierto: 1 = solo día, 2 = solo noche, 3 = día y noche
    const textoParte = (p) => ({ 1: " · solo día", 2: " · solo noche" }[Number(p)] || "");
    const textoSinCubrir = (x) => `${x.slot + 1}${x.parte === "dia" ? " (día)" : x.parte === "noche" ? " (noche)" : ""}`;

    return (
        <div className="container-fluid mt-4 mb-5">
            <h3 className="mb-1">Editar Acuerdos</h3>
            <div className="text-muted small mb-3">
                Cambia la rotación del paciente y qué auxiliar cubre cada slot desde una fecha. Antes de guardar
                verás exactamente qué acuerdos se cierran, cuáles se mantienen y cuáles se crean.
            </div>

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                    <BuscarPaciente onSelect={cargarPaciente} onClear={() => cargarPaciente(null)} conTurno />
                </div>
            </div>

            {cargando && <div className="text-center py-4">Cargando...</div>}

            {paciente && !cargando && (
                <>
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="mb-0 fw-bold">{paciente.nombres} {paciente.apellidoPaterno}</h5>
                            <span className="small text-muted">
                                {acuerdosActivos.length} acuerdo(s) activo(s)
                                {inicioRotacionActual && <> · Rotación desde {formatDate(inicioRotacionActual)}</>}
                            </span>
                        </div>
                        <div className="card-body">
                            <label className="form-label fw-bold">1. Auxiliares</label>
                            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                                {auxiliaresPalette.map(a => (
                                    <span key={a.id} className="badge d-flex align-items-center gap-2 px-3 py-2" style={{ background: a.color, fontSize: "0.85rem" }}>
                                        {a.nombre}
                                        <i className="bi bi-x-circle-fill" role="button" title="Quitar de la rotación" onClick={() => quitarAuxiliarPalette(a.id)}></i>
                                    </span>
                                ))}
                                <AgregarAuxiliarInline pacienteId={paciente.id} onAgregar={agregarAuxiliarPalette} />
                            </div>

                            <label className="form-label fw-bold">2. Días de servicio</label>
                            <div className="d-flex gap-2 flex-wrap mb-2">
                                {DIAS.map(d => (
                                    <button key={d.value} type="button" onClick={() => toggleDia(d.value)}
                                        className={`btn ${serviceDays.includes(d.value) ? "btn-primary" : "btn-outline-secondary"}`}
                                        style={{ width: 56, height: 48 }}>
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {PRESETS_DIAS.map(p => (
                                    <button key={p.id} type="button" className="btn btn-sm btn-link px-1" onClick={() => setServiceDays(p.dias)}>{p.label}</button>
                                ))}
                            </div>

                            <label className="form-label fw-bold">3. Turnos y quién los cubre</label>
                            <div className="d-flex gap-2 mb-3">
                                <button type="button" className={`btn btn-sm ${tipo === "fixed" ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setTipo("fixed")}>Fijo</button>
                                <button type="button" className={`btn btn-sm ${tipo === "rotative" ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setTipo("rotative")}>Rotativo</button>
                            </div>

                            {tipo === "fixed" ? (
                                <div className="d-flex align-items-center gap-2 flex-wrap py-2 px-2 border rounded" style={{ borderLeft: `6px solid ${colorDe(auxiliarFijo)}` }}>
                                    {PERIODO_OPCIONES_SLOT.filter(o => o.value !== "libre").map(op => (
                                        <button key={op.value} type="button" className={`btn btn-sm ${fixedPeriod === op.value ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setFixedPeriod(op.value)}>{op.label}</button>
                                    ))}
                                    <select
                                        className="form-select form-select-sm ms-md-3"
                                        style={{ width: 220, background: auxiliarFijo ? colorDe(auxiliarFijo) : undefined, color: auxiliarFijo ? "#fff" : undefined }}
                                        value={auxiliarFijo || ""}
                                        onChange={(e) => setAuxiliarFijo(e.target.value ? parseInt(e.target.value) : null)}
                                    >
                                        <option value="">Sin asignar</option>
                                        {auxiliaresPalette.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                                    </select>
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex flex-column gap-2 mb-2">
                                        {slots.map((slot, idx) => {
                                            const libre = esSlotLibre(slot);
                                            const mixto = esMixto(slot);
                                            const colorBorde = libre ? COLOR_DESCANSO : colorDe(mixto ? (slot.auxDia || slot.auxNoche) : slot.auxiliarId);
                                            return (
                                                <div key={idx} className="py-2 px-2 border rounded"
                                                    style={{ borderLeft: `6px solid ${colorBorde}`, background: libre ? "#f8f9fa" : undefined }}>
                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                        <div className="fw-bold small" style={{ width: 52 }}>Slot {idx + 1}</div>
                                                        <div style={{ width: 90 }}>
                                                            <div className="input-group input-group-sm">
                                                                <input type="number" min="1" className="form-control text-center" value={slot.days}
                                                                    onChange={(e) => updateSlot(idx, "days", parseInt(e.target.value) || 1)} title="Duración en días de servicio" />
                                                                <span className="input-group-text">d</span>
                                                            </div>
                                                        </div>
                                                        <select className="form-select form-select-sm" style={{ width: 170 }} value={slot.period} onChange={(e) => updateSlot(idx, "period", e.target.value)}>
                                                            {PERIODO_OPCIONES_SLOT.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                                                        </select>
                                                        {libre ? (
                                                            <span className="small text-muted fst-italic">Descanso de la rotación: sin auxiliar</span>
                                                        ) : mixto ? (
                                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                {mixtoTieneDia(slot) && (
                                                                    <div className="input-group input-group-sm" style={{ width: 230 }}>
                                                                        <span className="input-group-text" title="Quién cubre los turnos de día de este slot"><i className="bi bi-sun me-1"></i>Día</span>
                                                                        {selectorAuxiliar(slot.auxDia, (v) => updateSlot(idx, "auxDia", v))}
                                                                    </div>
                                                                )}
                                                                {mixtoTieneNoche(slot) && (
                                                                    <div className="input-group input-group-sm" style={{ width: 230 }}>
                                                                        <span className="input-group-text" title="Quién cubre los turnos de noche de este slot"><i className="bi bi-moon me-1"></i>Noche</span>
                                                                        {selectorAuxiliar(slot.auxNoche, (v) => updateSlot(idx, "auxNoche", v))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div style={{ width: 220 }}>{selectorAuxiliar(slot.auxiliarId, (v) => updateSlot(idx, "auxiliarId", v))}</div>
                                                        )}
                                                        <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={() => removeSlot(idx)} disabled={slots.length <= 1} title="Eliminar slot">
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                    {slot.period === "mixto" && (
                                                        <div className="mt-2 px-2 py-1 bg-light rounded small" style={{ marginLeft: 60, borderLeft: "3px solid #0d6efd" }}>
                                                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                                                <span className="fw-bold text-primary me-1" title="Qué se cubre cada día de este slot">Cada día:</span>
                                                                {Array.from({ length: parseInt(slot.days) || 0 }).map((_, d) => (
                                                                    <div key={d} className="d-flex align-items-center">
                                                                        <span className="text-muted me-1">D{d + 1}</span>
                                                                        <select className="form-select form-select-sm" style={{ width: 108 }}
                                                                            value={slot.dailyPeriods?.[d] || "full"}
                                                                            onChange={(e) => updateDiaMixto(idx, d, e.target.value)}>
                                                                            {OPCIONES_DIARIAS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                                                                        </select>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {mixto && slot.auxDia && slot.auxNoche && (
                                                                <span className="ms-2 fst-italic">
                                                                    {slot.auxDia === slot.auxNoche
                                                                        ? "· Un mismo auxiliar cubre día y noche."
                                                                        : "· Día y noche con auxiliares distintos."}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={addSlot}>
                                        <i className="bi bi-plus-circle me-1"></i>Agregar slot
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-body">
                            <label className="form-label fw-bold">4. Desde cuándo</label>
                            <div className="row g-3 align-items-end mb-3">
                                <div className="col-md-3">
                                    <label className="form-label small text-muted mb-1">Fecha efectiva</label>
                                    <input type="date" className="form-control" min={getFechaMinimaAcuerdo()} value={fechaEfectiva}
                                        onChange={(e) => { setFechaEfectiva(e.target.value); setPlan(null); }} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small text-muted mb-1" title="Día 1 del slot 1: desde aquí se cuentan los días de cada slot">
                                        Inicio de la rotación (slot 1, día 1)
                                    </label>
                                    <input type="date" className="form-control" max={fechaEfectiva} value={inicioRotacion}
                                        onChange={(e) => { setInicioRotacionElegido(e.target.value || null); setPlan(null); }} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1">Motivo (opcional)</label>
                                    <input className="form-control" value={observacion} onChange={(e) => setObservacion(e.target.value)} placeholder="Ej: cambio de auxiliar por renuncia" />
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-center small mb-3">
                                <span className="text-muted">Inicio de la rotación:</span>
                                <button type="button"
                                    className={`btn btn-sm ${inicioRotacion === fechaEfectiva ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => { setInicioRotacionElegido(fechaEfectiva); setPlan(null); }}>
                                    Partir el ciclo el {formatDate(fechaEfectiva)}
                                </button>
                                {inicioRotacionActual && inicioRotacionActual <= fechaEfectiva && (
                                    <button type="button"
                                        className={`btn btn-sm ${inicioRotacion === inicioRotacionActual ? "btn-primary" : "btn-outline-primary"}`}
                                        onClick={() => { setInicioRotacionElegido(inicioRotacionActual); setPlan(null); }}>
                                        Mantener el ciclo actual ({formatDate(inicioRotacionActual)})
                                    </button>
                                )}
                            </div>
                            <div className={`small mb-3 ${rotacionCambia ? "text-primary" : "text-muted"}`}>
                                {rotacionCambia
                                    ? <>El ciclo empieza el {formatDate(inicioRotacion)} (ese día es el día 1 del slot 1). Todos los acuerdos se rehacen desde el {formatDate(fechaEfectiva)}.</>
                                    : <>El ciclo continúa desde {formatDate(inicioRotacion)}. Solo cambia quién cubre cada slot.</>}
                            </div>

                            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                                <div className="fw-bold small"><i className="bi bi-eye me-2"></i>Vista previa</div>
                                <div className="d-flex align-items-center gap-1">
                                    {OPCIONES_SEMANAS.map(n => (
                                        <button key={n} type="button" className={`btn btn-sm ${semanas === n ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setSemanas(n)}>{n} sem.</button>
                                    ))}
                                </div>
                            </div>
                            <div style={{ overflowX: "auto" }}>
                                {Array.from({ length: semanas }).map((_, s) => (
                                    <div key={s} className="d-flex gap-2 mb-2" style={{ minWidth: 7 * 86 }}>
                                        {previewDias.slice(s * 7, s * 7 + 7).map((d, i) => {
                                            const libre = d.periodo === "libre";
                                            // Mitades del día: "full" = día y noche; si las cubren auxiliares
                                            // distintos (slot mixto) la celda se divide en dos colores.
                                            const mitades = d.periodo === "full"
                                                ? [{ aux: d.auxDia, icono: "bi-sun" }, { aux: d.auxNoche, icono: "bi-moon" }]
                                                : d.periodo === "day" ? [{ aux: d.auxDia, icono: "bi-sun" }]
                                                    : d.periodo === "night" ? [{ aux: d.auxNoche, icono: "bi-moon" }] : [];
                                            const dividida = mitades.length === 2 && mitades[0].aux !== mitades[1].aux;
                                            const partes = dividida ? mitades : mitades.slice(0, 1);
                                            return (
                                                <div key={i} className="text-center" style={{ width: 80, opacity: d.antes ? 0.35 : 1 }} title={d.antes ? "Antes de la fecha efectiva (no cambia)" : ""}>
                                                    <div className="small text-muted mb-1">{DIAS[(d.fecha.getDay() + 6) % 7].label} {d.fecha.getDate()}</div>
                                                    <div className="rounded overflow-hidden d-flex flex-column"
                                                        style={{ height: 56, fontSize: "0.68rem", fontWeight: 600, border: "1px solid rgba(0,0,0,0.06)" }}>
                                                        {partes.length === 0 ? (
                                                            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center"
                                                                style={{ background: libre ? COLOR_DESCANSO : "#f8f9fa", color: "#6c757d" }}>
                                                                {d.slot !== null && <div style={{ fontSize: "0.6rem", opacity: 0.85 }}>Slot {d.slot}</div>}
                                                                <div>{libre ? "Descanso" : "—"}</div>
                                                            </div>
                                                        ) : partes.map((m, k) => (
                                                            <div key={k} className="flex-grow-1 d-flex flex-column align-items-center justify-content-center"
                                                                style={{ background: m.aux ? colorDe(m.aux) : "#ffe3e3", color: m.aux ? "#fff" : "#c92a2a", lineHeight: 1.15 }}>
                                                                {!dividida && <div style={{ fontSize: "0.6rem", opacity: 0.85 }}>Slot {d.slot} · {LABEL_PERIODO[d.periodo]}</div>}
                                                                <div>
                                                                    {dividida && <i className={`bi ${m.icono} me-1`}></i>}
                                                                    {m.aux ? nombreDe(m.aux).split(" ")[0] : "Sin cubrir"}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                            <div className="small text-muted mt-1">
                                <span className="me-3"><span className="d-inline-block rounded me-1" style={{ width: 10, height: 10, background: COLOR_DESCANSO }}></span>Descanso</span>
                                <span className="me-3"><span className="d-inline-block rounded me-1" style={{ width: 10, height: 10, background: "#ffe3e3" }}></span>Turno sin auxiliar</span>
                                <span><i className="bi bi-sun me-1"></i><i className="bi bi-moon me-1"></i>Celda dividida: día y noche con auxiliares distintos</span>
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                                <button type="button" className="btn btn-success" onClick={revisarCambios} disabled={procesando}>
                                    <i className="bi bi-check2-square me-2"></i>{procesando && !plan ? "Revisando..." : "Revisar y guardar"}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {plan && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog modal-lg modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirmar cambios desde el {formatDate(plan.fecha)}</h5>
                                    <button type="button" className="btn-close" onClick={() => setPlan(null)} disabled={procesando}></button>
                                </div>
                                <div className="modal-body">
                                    <p className="mb-3">
                                        {plan.rotacionCambia
                                            ? <><b>{plan.patronCambia ? "La rotación cambia" : "El ciclo de la rotación se reinicia"}</b> y empieza el {formatDate(plan.inicioRotacion)} (día 1 del slot 1).</>
                                            : <>La rotación no cambia; solo cambia quién cubre algunos slots.</>}
                                        {" "}Nada antes del {formatDate(plan.fecha)} se modifica.
                                    </p>

                                    {plan.cerrar.length > 0 && (
                                        <div className="mb-3">
                                            <div className="fw-semibold text-danger mb-1"><i className="bi bi-x-circle me-1"></i>Se cierran</div>
                                            <ul className="small mb-0">
                                                {plan.cerrar.map(a => <li key={a.idAcuerdo}>{a.auxiliarNombre} — acuerdo #{a.idAcuerdo} (slots {listaSlots(a.slots)}{textoParte(a.periodoCubierto)})</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {plan.crear.length > 0 && (
                                        <div className="mb-3">
                                            <div className="fw-semibold text-success mb-1"><i className="bi bi-plus-circle me-1"></i>Se crean</div>
                                            <ul className="small mb-0">
                                                {plan.crear.map(a => (
                                                    <li key={`${a.idAuxiliar}-${a.periodoCubierto}`}>
                                                        {a.auxiliarNombre} — slots {listaSlots(a.slots)}{textoParte(a.periodoCubierto)} · turno ${formatMoney(a.valorTurno)}
                                                        {a.valorColacion > 0 && <> · colación ${formatMoney(a.valorColacion)}</>}
                                                        {a.valorMovilizacion > 0 && <> · movilización ${formatMoney(a.valorMovilizacion)}</>}
                                                        <span className="text-muted"> (valores del {a.origenValores})</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {plan.mantener.length > 0 && (
                                        <div className="mb-3">
                                            <div className="fw-semibold text-secondary mb-1"><i className="bi bi-check-circle me-1"></i>Se mantienen sin cambios</div>
                                            <ul className="small mb-0">
                                                {plan.mantener.map(a => <li key={a.idAcuerdo}>{a.auxiliarNombre} — acuerdo #{a.idAcuerdo} (slots {listaSlots(a.slots)}{textoParte(a.periodoCubierto)})</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {plan.slotsSinAuxiliar.length > 0 && (
                                        <div className="alert alert-warning small mb-0">
                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                            Quedan turnos sin auxiliar: slot(s) {plan.slotsSinAuxiliar.map(textoSinCubrir).join(", ")}. Se pueden asignar después.
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setPlan(null)} disabled={procesando}>Volver</button>
                                    <button type="button" className="btn btn-success" onClick={aplicar} disabled={procesando}>
                                        {procesando ? "Guardando..." : "Confirmar y guardar"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default GestionSlotsDemo;
